import React, { useState } from 'react';
import { orderTrackingService } from '../../../services/orderTrackingService';
import Icon from '../../../components/AppIcon';

const BulkShippingPanel = ({ orders, carriers, onComplete }) => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const toggleOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev?.includes(orderId)
        ? prev?.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectAll = () => {
    if (selectedOrders?.length === orders?.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders?.map(o => o?.id));
    }
  };

  const handleBulkShip = async () => {
    if (selectedOrders?.length === 0) {
      setError('Lütfen en az bir sipariş seçin');
      return;
    }

    if (!selectedCarrier) {
      setError('Lütfen bir kargo firması seçin');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      setSuccess('');

      const carrier = carriers?.find(c => c?.carrierCode === selectedCarrier);
      let successCount = 0;

      for (const orderId of selectedOrders) {
        const order = orders?.find(o => o?.id === orderId);
        if (!order) continue;

        try {
          const trackingNumber = `TRK${Date.now()}${Math.random()?.toString(36)?.substring(7)?.toUpperCase()}`;
          const trackingUrl = carrier?.trackingUrlTemplate?.replace('{tracking_number}', trackingNumber);
          
          const estimatedDelivery = new Date();
          estimatedDelivery?.setDate(estimatedDelivery?.getDate() + (carrier?.averageDeliveryDays || 3));

          await orderTrackingService?.createShipment({
            orderId: order?.id,
            carrier: selectedCarrier,
            trackingNumber,
            carrierTrackingUrl: trackingUrl,
            shippingMethod: 'standard',
            estimatedDeliveryDate: estimatedDelivery?.toISOString(),
            shippingCost: carrier?.costPerKg * 2,
            deliveryAddress: order?.shippingAddress
          });

          successCount++;
        } catch (err) {
          console.error(`Error shipping order ${orderId}:`, err);
        }
      }

      setSuccess(`${successCount} sipariş başarıyla kargoya verildi`);
      setSelectedOrders([]);
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err) {
      console.error('Bulk shipping error:', err);
      setError(err?.message || 'Toplu kargolama sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Toplu Kargolama
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Birden fazla siparişi aynı anda kargoya verin
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <Icon name="CheckCircle" size={20} />
            <span>{success}</span>
          </div>
        )}

        {/* Carrier Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Kargo Firması Seçin
          </label>
          <select
            value={selectedCarrier}
            onChange={(e) => setSelectedCarrier(e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Kargo firması seçin</option>
            {carriers?.map((carrier) => (
              <option key={carrier?.id} value={carrier?.carrierCode}>
                {carrier?.carrierName} - {carrier?.averageDeliveryDays} gün - {carrier?.costPerKg} TL/kg
              </option>
            ))}
          </select>
        </div>

        {/* Order Selection */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-sm font-medium text-foreground">
              Siparişleri Seçin ({selectedOrders?.length}/{orders?.length})
            </label>
            <button
              onClick={selectAll}
              className="text-sm text-primary hover:underline"
            >
              {selectedOrders?.length === orders?.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {orders?.map((order) => (
              <label
                key={order?.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedOrders?.includes(order?.id)}
                  onChange={() => toggleOrder(order?.id)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    Sipariş #{order?.id?.substring(0, 8)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {order?.items?.length} ürün - {order?.shippingAddress?.city}
                  </p>
                </div>
                <span className="text-sm font-medium text-foreground">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' })?.format(order?.totalAmount)}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleBulkShip}
          disabled={processing || selectedOrders?.length === 0 || !selectedCarrier}
          className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <>
              <Icon name="Loader" size={20} className="animate-spin" />
              <span>İşleniyor...</span>
            </>
          ) : (
            <>
              <Icon name="Truck" size={20} />
              <span>Seçili Siparişleri Kargoya Ver ({selectedOrders?.length})</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BulkShippingPanel;